import React, { useState, useEffect, useRef } from "react";
import { Button } from "@back4app2/react-components";
import back4app2 from "../back4app2";
import { ReactComponent as CursorIconSVG } from "../assets/images/cursor-icon.svg";
import { ReactComponent as WindsurfIconSVG } from "../assets/images/windsurf-icon.svg";
import { ReactComponent as VSCodeIconSVG } from "../assets/images/vscode-icon.svg";
import { ReactComponent as CopyIconSVG } from "../assets/images/copy-icon.svg";
import { ReactComponent as CheckIconSVG } from "../assets/images/b4a-check-icon.svg";
import { BACK4APP_DOT_COM_SITE_URL } from "../settings";
import MarkdownText from "./MarkdownText/MarkdownText";
import { pushGTMEvent } from '../utils/gtm';
import Cookies from "js-cookie";

// Utility function to copy text to clipboard
const copyToClipboard = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    // You could add a toast notification here
  } catch (err) {
    console.error("Failed to copy text: ", err);
  }
};

const MCPSignUpSetup = ({ onComplete }: { onComplete: () => void }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedIDE, setSelectedIDE] = useState("cursor");
  const [mcpKey, setMcpKey] = useState("YOUR_ACCOUNT_KEY");
  const [automatic, setAutomatic] = useState(true);

  // Refs for each step section
  const step1Ref = useRef<HTMLDivElement>(null);
  const step2Ref = useRef<HTMLDivElement>(null);
  const step3Ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Get MCP key from API or context
    const getMcpKey = async () => {
      try {
        const key = await back4app2.getMcpKey();
        setMcpKey(key);
      } catch (error) {
        console.error("Failed to get MCP key: ", error);
      }
    };
    getMcpKey();

    // Cleanup function to remove cookie when component unmounts
    return () => {
      Cookies.remove("signupSetupFlow", { domain: "back4app.com" });
    };
  }, []);

  const handleIDESelection = (ide: string) => {
    setSelectedIDE(ide);
    setCurrentStep(2);
    if (step2Ref?.current) {
      step2Ref.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });
    }
  };

  const generateDeeplink = (ide: string, accountKey: string) => {
    if (ide === "cursor") {
      const config = {
        command: "npx",
        args: [
          "-y",
          "@back4app/mcp-server-back4app@latest",
          "--account-key",
          accountKey
        ]
      };
      // Convert to base64 using browser-compatible method
      const base64Config = btoa(JSON.stringify(config));
      return `cursor://anysphere.cursor-deeplink/mcp/install?name=back4app&config=${encodeURIComponent(base64Config)}`;
    } else if (ide === "vscode") {
      const config = {
        name: "back4app",
        command: "npx",
        args: [
          "-y",
          "@back4app/mcp-server-back4app@latest",
          "--account-key",
          accountKey
        ]
      };
      const encoded = encodeURIComponent(JSON.stringify(config));
      return `vscode:mcp/install?${encoded}`;
    }
    return "";
  };

  const getManualJsonContent = (
    ide: string,
    mcpKey: string,
    isLinux: boolean
  ): string => {
    if (ide === "cursor" && isLinux) {
      return `\`\`\`js
{
  "mcpServers": {
    "back4app": {
      "command": "npx",
      "args": [
        "-y",
        "@back4app/mcp-server-back4app@latest",
        "--account-key",
        "${mcpKey}"
      ]
    }
  }
}`;
    } else if (ide === "cursor" && !isLinux) {
      return `\`\`\`js
{
  "mcpServers": {
    "back4app": {
      "command": "npx",
      "args": [
        "-y",
        "@back4app/mcp-server-back4app@latest",
        "--account-key",
        "${mcpKey}"
      ]
    }
  }
}
`;
    } else if (ide === "windsurf" && isLinux) {
      return `\`\`\`js
{
  "mcpServers": {
    "back4app": {
      "command": "npx",
      "args": [
        "-y",
        "@back4app/mcp-server-back4app@latest",
        "--account-key",
        "${mcpKey}"
      ]
    }
  }
}
`;
    } else if (ide === "windsurf" && !isLinux) {
      return `\`\`\`js
{
  "mcpServers": {
    "back4app": {
      "command": "npx",
      "args": [
        "-y",
        "@back4app/mcp-server-back4app@latest",
        "--account-key",
        "${mcpKey}"
      ]
    }
  }
}
`;
    } else if (ide === "vscode" && isLinux) {
      return `\`\`\`js
{
  "inputs": [
    {
      "type": "promptString",
      "id": "back4app-account-key",
      "description": "Back4App personal access token",
      "password": true
    }
  ],
  "servers": {
    "back4app": {
      "command": "npx",
      "args": ["-y", "@back4app/mcp-server-back4app@latest"],
      "env": {
        "BACK4APP_ACCOUNT_KEY": "\${input:back4app-account-key}"
      }
    }
  }
}
`;
    } else if (ide === "vscode" && !isLinux) {
      return `\`\`\`js
{
  "inputs": [
    {
      "type": "promptString",
      "id": "back4app-account-key",
      "description": "Back4App personal access token",
      "password": true
    }
  ],
  "servers": {
    "back4app": {
      "command": "npx",
      "args": ["-y", "@back4app/mcp-server-back4app@latest"],
      "env": {
        "BACK4APP_ACCOUNT_KEY": "\${input:back4app-account-key}"
      }
    }
  }
}
`;
    }
    return "";
  };

  const getManualInstructions = (ide: string) => {
    if (ide === "cursor") {
      return (
        <>
          <div className='text-lg mb-2'>1. Open MCP Settings</div>
          <div className='text-sm font-inter'>
            Navigate to:{" "}
            <strong>Cursor → Full Settings → Tools & Integrations → MCP</strong>
          </div>
          <div className='mt-4'></div>
          <div className='text-lg mb-2'>2. Add MCP Server Configuration</div>
          <div className='text-sm font-inter'>
            Click <strong>'+ Add new global MCP server'</strong> button.
          </div>
          <div className='text-sm font-inter'>
            Copy & Paste the code below into opened '<strong>mcp.json</strong>'
            file
          </div>
        </>
      );
    } else if (ide === "windsurf") {
      return (
        <>
          <div className='text-lg mb-2'>1. Open MCP Settings</div>
          <div className='text-sm font-inter'>
            Navigate to: <strong>Windsurf → Cascade assistant</strong>
          </div>
          <div className='mt-4'></div>
          <div className='text-lg mb-2'>2. Add MCP Server Configuration</div>
          <div className='text-sm font-inter'>
            Tap on the hammer (MCP) icon, then Configure to open the
            configuration file
          </div>
          <div className='text-sm font-inter mt-2'>
            Add the following configuration:
          </div>
        </>
      );
    } else if (ide === "vscode") {
      return (
        <>
          <div className='text-lg mb-2'>1. Open MCP Settings</div>
          <div className='text-sm font-inter'>
            Navigate to:{" "}
            <strong>
              VSCode(Copilot) → Go to root directory of your project
            </strong>
          </div>
          <div className='text-sm font-inter'>
            Create a .vscode folder if it doesn't exist
          </div>
          <div className='mt-4'></div>
          <div className='text-lg mb-2'>2. Add MCP Server Configuration</div>
          <div className='text-sm font-inter'>
            Create a mcp.json file in the .vscode folder
          </div>
          <div className='text-sm font-inter mt-2'>
            Add the following configuration:
          </div>
        </>
      );
    }
    return null;
  };

  const getIDESetupContent = () => {
    const setupCommands: Record<string, string> = {
      cursor: `npx @back4app/mcp-installer install cursor --account-key ${mcpKey}`,
      windsurf: `npx @back4app/mcp-installer install windsurf --account-key ${mcpKey}`,
      vscode: `npx @back4app/mcp-installer install vscode --account-key ${mcpKey}`,
    };

    const verifyInstructions: Record<string, string> = {
      cursor: "Cursor > Full Settings > Tools & Integrations",
      windsurf:
        "Windsurf > Find the toolbar above the Cascade input and click on refresh",
      vscode:
        "VSCode > Click on configure tools on the Agent Mode (Copilot) and find the back4app MCP tools.",
    };

    return (
      <div className='space-y-6'>
        <div className='rounded-md overflow-hidden mb-6 inline-flex relative border border-light-grey/50'>
          {/* Sliding background indicator */}
          <div
            className={`absolute top-0 bottom-0 bg-cta-green rounded-md transition-all duration-300 ease-in-out ${
              automatic ? "left-0 w-[85px]" : "left-[85px] w-[100px]"
            }`}
          />
          <button
            onClick={() => setAutomatic(true)}
            className={`relative px-4 py-2 rounded-md font-medium transition-all duration-200 z-10 text-xs ${
              automatic ? "text-white" : "text-gray-300 hover:text-white"
            }`}
          >
            Automatic
          </button>
          <button
            onClick={() => setAutomatic(false)}
            className={`relative px-4 py-2 rounded-md font-medium transition-all duration-200 z-10 text-xs ${
              !automatic ? "text-white" : "text-gray-300 hover:text-white"
            }`}
          >
            Manual
          </button>
        </div>

        {automatic ? (
          <div className='space-y-6'>
            {(selectedIDE === "cursor" || selectedIDE === "vscode") && (
              <div>
                <h3 className='text-lg mb-2'>1. Try One-Click Install First</h3>
                <p className='mb-4 font-inter text-sm text-light-grey'>
                  Click the button below to automatically install the Back4App MCP server in {selectedIDE}.
                </p>
                <a
                  href={generateDeeplink(selectedIDE, mcpKey)}
                  className='inline-flex items-center px-4 py-2 bg-cta-green text-white rounded-md hover:bg-green-600 transition-colors text-sm font-medium'
                >
                  Add to {selectedIDE.charAt(0).toUpperCase() + selectedIDE.slice(1)}
                </a>
              </div>
            )}
            
            <div>
              <h3 className='text-lg mb-2'>{(selectedIDE === "cursor" || selectedIDE === "vscode") ? "2. Alternative: Run the installation command" : "1. Run the installation command"}</h3>
              <p className='mb-4 font-inter text-sm text-light-grey'>
                {(selectedIDE === "cursor" || selectedIDE === "vscode") ? 
                  "If the one-click install doesn't work, copy and run the command below in your terminal:" :
                  `Copy and run the command below in your terminal to install ${selectedIDE}:`
                }
              </p>
              <CommandBlock command={setupCommands[selectedIDE]} />
            </div>

            <div>
              <h3 className='text-lg mb-2'>{(selectedIDE === "cursor" || selectedIDE === "vscode") ? "3" : "2"}. Verify your connection</h3>
              <div className='text-sm font-inter'>
                Navigate to: <strong>{verifyInstructions[selectedIDE]}</strong>
              </div>
            </div>
          </div>
        ) : (
          <div className=''>
            {getManualInstructions(selectedIDE)}
            <div className='space-y-4 mt-2'>
              <CodeBlock
                value={getManualJsonContent(selectedIDE, mcpKey, false)}
                systemName='macOS / Linux'
              />
              <CodeBlock
                value={getManualJsonContent(selectedIDE, mcpKey, true)}
                systemName='Windows'
              />
            </div>
          </div>
        )}

        <div className='mt-4'>
          <Button
            type='primary'
            value='Next'
            onClick={() => {
              setCurrentStep(3);
              if (step3Ref?.current) {
                step3Ref.current.scrollIntoView({
                  behavior: "smooth",
                  block: "start",
                  inline: "nearest",
                });
              }
            }}
          />
        </div>
      </div>
    );
  };

  const getAppCreationContent = () => {
    return (
      <div className='space-y-6'>
        <div>
          <div className='text-lg mb-2'>1. Tell your agent what you need</div>
          <p className='font-inter text-sm mb-4'>
            In your AI agent chat, You can use Back4App MCP to interact with
            your Back4App account.
            <br />
            Here is an example to get a list of your apps:
          </p>
          <CommandBlock command='Create a new ToDoList App frontend and backend on back4app.' />
        </div>

        <div>
          <div className='text-lg mb-2'>
            2. Refer to docs for more information
          </div>
          <a
            href={`${BACK4APP_DOT_COM_SITE_URL}/docs/mcp`}
            className='text-cta-green underline underline-offset-2 text-sm'
            target='_blank'
            rel='noopener noreferrer'
          >
            {BACK4APP_DOT_COM_SITE_URL}/docs/mcp
          </a>
        </div>

        <div className='mt-4'>
          <Button 
          type='primary' 
          value='Go to My Apps' 
          onClick={()=>{
            pushGTMEvent('MCP Activation',true);
            onComplete();
          }} 
          />
        </div>
      </div>
    );
  };

  const renderStepIndicator = (
    stepNumber: number,
    isCompleted: boolean = false,
    isCurrent: boolean = false
  ) => {
    return (
      <div className='flex flex-col items-center space-y-2'>
        <div
          className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold relative ${
            isCompleted ? "bg-green-600 text-white" : "text-white"
          }`}
        >
          {!isCompleted && (
            <div
              className='absolute transform scale-110 w-full h-full rounded-full'
              style={{
                background: "linear-gradient(90deg, #208AEC 0%, #27AE60 100%)",
                padding: "2px",
              }}
            >
              <div className='w-full h-full bg-dark rounded-full' />
            </div>
          )}
          {isCompleted ? (
            <svg className='w-5 h-5' fill='currentColor' viewBox='0 0 20 20'>
              <path
                fillRule='evenodd'
                d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                clipRule='evenodd'
              />
            </svg>
          ) : (
            <span className='relative z-10'>{stepNumber}</span>
          )}
        </div>
        {stepNumber < 3 && (
          <div
            className={`flex-1 w-px h-full mx-auto ${
              isCompleted ? "bg-cta-green" : "bg-light-grey/50"
            }`}
          ></div>
        )}
      </div>
    );
  };

  return (
    <div className='h-full overflow-y-auto pb-20'>
      <div className='max-w-5xl mx-4 md:mx-6 lg:mx-auto'>
        {/* Header */}
        <div className='pt-20 pb-8'>
          <h1 className='text-3xl font-bold mb-4'>MCP Setup</h1>
          <p className='text-sm text-light-grey'>
            Welcome to Back4App! Let&apos;s get started by configuring the MCP
            Server on your preferred IDE.
          </p>
        </div>

        <div className='flex-1 space-y-8'>
          <div className='flex space-x-8' ref={step1Ref}>
            {renderStepIndicator(1, currentStep > 1, currentStep === 1)}
            <div>
              <h2 className='text-2xl font-semibold mb-4'>Select Your IDE</h2>
              <p className='text-gray-400 mb-4 text-sm font-inter'>
                Pick the IDE you want to use with the back4app MCP
              </p>

              <div className='flex gap-4'>
                {[
                  {
                    id: "cursor",
                    name: "Cursor",
                    icon: <CursorIconSVG className='w-16 h-w-16' />,
                  },
                  {
                    id: "vscode",
                    name: "VSCode",
                    icon: <VSCodeIconSVG className='w-16 h-w-16' />,
                  },
                  {
                    id: "windsurf",
                    name: "Windsurf",
                    icon: <WindsurfIconSVG className='w-16 h-w-16' />,
                  },
                ].map((ide) => (
                  <button
                    key={ide.id}
                    onClick={() => handleIDESelection(ide.id)}
                    className={`p-2 min-w-[140px] rounded-lg border-2 border-light-blue/20 transition-all bg-regal-blue flex items-center flex-col justify-center gap-2 ${
                      selectedIDE === ide.id
                        ? "border-blue-500 bg-blue-600/20"
                        : "border-gray-600 bg-gray-700 hover:border-gray-500"
                    }`}
                  >
                    {ide.icon}
                    <div className='font-semibold text-xs'>{ide.name}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className={`flex space-x-8 ${currentStep > 1 ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300 delay-100`} ref={step2Ref}>
            {renderStepIndicator(2, currentStep > 2, currentStep === 2)}
            <div className="flex-[0.8]">
              <h2 className='text-2xl font-semibold mb-4'>
                {selectedIDE.charAt(0).toUpperCase() + selectedIDE.slice(1)} MCP
                Setup
              </h2>
              <p className='mb-4 font-inter text-sm text-light-grey'>
                Follow the steps below to get MCP working with {selectedIDE}.
              </p>
              {getIDESetupContent()}
            </div>
          </div>
          <div className={`flex space-x-8 ${currentStep > 2 ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300 delay-100`} ref={step3Ref}>
            {renderStepIndicator(3, false, currentStep === 3)}
            <div>
              <h2 className='text-2xl font-semibold mb-4'>
                Test your connection
              </h2>
              {getAppCreationContent()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MCPSignUpSetup;

const CommandBlock = ({ command }: { command: string }) => {
  const [copied, setCopied] = useState(false);
  return (
    <div className='bg-regal-blue rounded-md px-2 flex items-center justify-between'>
      <code className='text-light-blue font-mono text-xs'>{command}</code>
      <button
        onClick={() => {
          copyToClipboard(command);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        }}
        className='ml-4 p-2 text-gray-400 hover:text-white transition-colors'
      >
        {copied ? (
          <CheckIconSVG className='text-cta-green' />
        ) : (
          <CopyIconSVG className='text-light-blue' />
        )}
      </button>
    </div>
  );
};

const CodeBlock = ({
  value,
  systemName,
}: {
  value: string;
  systemName: string;
}) => {
  const [copied, setCopied] = useState(false);
  return (
    <div>
      <div className='text-sm font-medium mb-2'>{systemName}</div>
      <div className='flex justify-between items-center bg-regal-blue rounded-md px-4 py-2 rounded-b-none'>
        mcp.json
        <button
          onClick={() => {
            copyToClipboard(value.replace("```js", "").replace("```", ""));
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          }}
          className='ml-4 p-2 text-gray-400 hover:text-white transition-colors'
        >
          {copied ? (
            <CheckIconSVG className='text-cta-green' />
          ) : (
            <CopyIconSVG className='text-light-blue' />
          )}
        </button>
      </div>
      <MarkdownText message={{ content: value }} />
    </div>
  );
};
